import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  useBusinessHoursConfig,
  useHourlyStatus,
  useDailySummary,
  useUpdateBusinessHours,
  type HourlyStatus,
} from '../hooks/useBusinessHours';
import { useSalesTargetConfig } from '../hooks/useSalesTargets';
import {
  useDistributionsSummaryByDay,
  useHourlyDistributionsByDay,
  useDistributionAlertByDay,
  useCreateHourlyDistribution,
  useUpdateHourlyDistribution,
  useDeleteHourlyDistribution,
  useDeleteHourlyDistributionsByDay,
  useDistributeUniformly,
  useCopyDistributions,
  useResetAllHourlyDistributions,
  type HourlyDistribution,
  type DayOfWeek,
  dayOfWeekLabels,
  dayOfWeekShortLabels,
  dayOfWeekOrder,
  formatTime as formatDistributionTime,
  getCurrentDayOfWeek,
} from '../hooks/useHourlyDistribution';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type TabType = 'horarios' | 'distribucion';

export const BusinessHoursPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('horarios');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Hourly Distribution states
  const [selectedDay, setSelectedDay] = useState<DayOfWeek>(getCurrentDayOfWeek());
  const [showDistCreateModal, setShowDistCreateModal] = useState(false);
  const [editingDist, setEditingDist] = useState<HourlyDistribution | null>(null);
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [distributeConfig, setDistributeConfig] = useState({
    businessStartHour: 7,
    businessEndHour: 22,
    clearExisting: true,
  });
  const [copyConfig, setCopyConfig] = useState({
    toDays: [] as DayOfWeek[],
    overwrite: false,
  });
  const [distFormData, setDistFormData] = useState({
    startHour: 12,
    startMinute: 0,
    endHour: 14,
    endMinute: 0,
    percentage: 20,
    description: '',
  });

  // Get business hours config
  const { config, loading: configLoading, refetch: refetchConfig } = useBusinessHoursConfig();

  // Get sales target config for daily target
  const { config: salesConfig, loading: salesLoading } = useSalesTargetConfig();

  // Get hourly status and daily summary
  const dailyTarget = salesConfig?.defaultAmount || 0;
  const { status: hourlyStatus, loading: statusLoading, refetch: refetchStatus } = useHourlyStatus(selectedDate, dailyTarget, 3000);
  const { summary, loading: _summaryLoading, refetch: refetchSummary } = useDailySummary(selectedDate, dailyTarget, 3000);

  // Debug log
  console.log('[BusinessHoursPanel] Debug:', {
    dailyTarget,
    selectedDateISO: selectedDate.toISOString(),
    selectedDateLocal: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth()+1).padStart(2,'0')}-${String(selectedDate.getDate()).padStart(2,'0')}`,
    hourlyStatusLength: hourlyStatus.length,
    summaryTotalSales: summary?.totalSales,
    firstHourSales: hourlyStatus[0]?.salesAmount,
    statusLoading,
  });

  const { updateConfig, updating } = useUpdateBusinessHours();

  // Hourly Distribution queries
  const { summary: distSummary, loading: _loadingDistSummary, refetch: refetchDistSummary } = useDistributionsSummaryByDay();
  const { distributions, loading: loadingDistributions, refetch: refetchDistributions } = useHourlyDistributionsByDay(selectedDay);
  const { alert: distAlert, refetch: refetchDistAlert } = useDistributionAlertByDay(selectedDay);

  // Hourly Distribution mutations
  const { create: createDist, creating: creatingDist } = useCreateHourlyDistribution();
  const { update: updateDist, updating: updatingDist } = useUpdateHourlyDistribution();
  const { deleteDistribution, deleting: deletingDist } = useDeleteHourlyDistribution();
  const { deleteByDay, deleting: deletingByDay } = useDeleteHourlyDistributionsByDay();
  const { distribute, distributing } = useDistributeUniformly();
  const { copy, copying } = useCopyDistributions();
  const { reset, resetting } = useResetAllHourlyDistributions();

  // Config form state
  const [formData, setFormData] = useState({
    startHour: 11,
    startMinute: 0,
    endHour: 22,
    endMinute: 0,
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
  });

  // Update form when config loads
  useEffect(() => {
    if (config) {
      setFormData({
        startHour: config.startHour,
        startMinute: config.startMinute,
        endHour: config.endHour,
        endMinute: config.endMinute,
        monday: config.monday,
        tuesday: config.tuesday,
        wednesday: config.wednesday,
        thursday: config.thursday,
        friday: config.friday,
        saturday: config.saturday,
        sunday: config.sunday,
      });
    }
  }, [config]);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      refetchStatus();
      refetchSummary();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refetchStatus, refetchSummary]);

  const formatTime = (hour: number, minute: number) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = minute.toString().padStart(2, '0');
    return `${displayHour}:${displayMinute} ${period}`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCompactCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return formatCurrency(amount);
  };

  const handleSaveConfig = async () => {
    try {
      await updateConfig(formData);
      setShowConfigModal(false);
      refetchConfig();
      refetchStatus();
      refetchSummary();
    } catch (error) {
      console.error('Error updating config:', error);
    }
  };

  // Distribution handlers
  const refetchAllDist = async () => {
    await Promise.all([refetchDistSummary(), refetchDistributions(), refetchDistAlert()]);
  };

  useEffect(() => {
    if (editingDist) {
      setDistFormData({
        startHour: editingDist.startHour,
        startMinute: editingDist.startMinute,
        endHour: editingDist.endHour,
        endMinute: editingDist.endMinute,
        percentage: editingDist.percentage,
        description: editingDist.description || '',
      });
    }
  }, [editingDist]);

  const handleCreateDist = async () => {
    try {
      await createDist({ dayOfWeek: selectedDay, ...distFormData });
      setShowDistCreateModal(false);
      setDistFormData({ startHour: 12, startMinute: 0, endHour: 14, endMinute: 0, percentage: 20, description: '' });
      await refetchAllDist();
    } catch (error: any) {
      console.error('Error creating distribution:', error);
      window.alert(error.message || 'Error al crear la distribución');
    }
  };

  const handleUpdateDist = async () => {
    if (!editingDist) return;
    try {
      await updateDist({ id: editingDist.id, ...distFormData });
      setEditingDist(null);
      setDistFormData({ startHour: 12, startMinute: 0, endHour: 14, endMinute: 0, percentage: 20, description: '' });
      await refetchAllDist();
    } catch (error: any) {
      console.error('Error updating distribution:', error);
      window.alert(error.message || 'Error al actualizar la distribución');
    }
  };

  const handleDeleteDist = async (id: string) => {
    if (!window.confirm('¿Eliminar esta distribución?')) return;
    try {
      await deleteDistribution(id);
      await refetchAllDist();
    } catch (error: any) {
      console.error('Error deleting distribution:', error);
      window.alert(error.message || 'Error al eliminar');
    }
  };

  const handleDeleteByDay = async () => {
    if (!window.confirm(`¿Eliminar TODAS las distribuciones de ${dayOfWeekLabels[selectedDay]}?`)) return;
    try {
      await deleteByDay(selectedDay);
      await refetchAllDist();
    } catch (error: any) {
      console.error('Error deleting by day:', error);
      window.alert(error.message || 'Error al eliminar');
    }
  };

  const handleToggleDistActive = async (dist: HourlyDistribution) => {
    try {
      await updateDist({ id: dist.id, isActive: !dist.isActive });
      await refetchAllDist();
    } catch (error: any) {
      console.error('Error toggling distribution:', error);
    }
  };

  const handleDistributeUniformly = async () => {
    try {
      await distribute({
        dayOfWeek: selectedDay,
        businessStartHour: distributeConfig.businessStartHour,
        businessEndHour: distributeConfig.businessEndHour,
        clearExisting: distributeConfig.clearExisting,
      });
      await refetchAllDist();
      setShowDistributeModal(false);
    } catch (error: any) {
      console.error('Error distributing:', error);
      window.alert(error.message || 'Error al distribuir');
    }
  };

  const handleCopyDist = async () => {
    if (copyConfig.toDays.length === 0) {
      window.alert('Selecciona al menos un día');
      return;
    }
    try {
      await copy({ fromDay: selectedDay, toDays: copyConfig.toDays, overwrite: copyConfig.overwrite });
      await refetchAllDist();
      setShowCopyModal(false);
      setCopyConfig({ toDays: [], overwrite: false });
    } catch (error: any) {
      console.error('Error copying:', error);
      window.alert(error.message || 'Error al copiar');
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('¿Eliminar TODAS las distribuciones de TODOS los días?')) return;
    try {
      await reset();
      await refetchAllDist();
    } catch (error: any) {
      console.error('Error resetting:', error);
      window.alert(error.message || 'Error al resetear');
    }
  };

  const closeDistModal = () => {
    setShowDistCreateModal(false);
    setEditingDist(null);
    setDistFormData({ startHour: 12, startMinute: 0, endHour: 14, endMinute: 0, percentage: 20, description: '' });
  };

  const toggleCopyDay = (day: DayOfWeek) => {
    if (day === selectedDay) return;
    setCopyConfig(prev => ({
      ...prev,
      toDays: prev.toDays.includes(day) ? prev.toDays.filter(d => d !== day) : [...prev.toDays, day],
    }));
  };

  const currentDayDistSummary = distSummary.find(s => s.dayOfWeek === selectedDay);

  const getStatusColor = (hourStatus: HourlyStatus) => {
    if (hourStatus.isAhead) return 'ahead';
    if (hourStatus.isOnTrack) return 'on-track';
    return 'behind';
  };

  // Prepare chart data
  const prepareLineChartData = () => {
    const labels = hourlyStatus.map(h => `${h.hour}:00`);
    const salesData = hourlyStatus.map(h => h.salesAmount);
    const targetData = hourlyStatus.map(h => h.adaptiveTargetPerHour || h.targetPerHour);

    return {
      labels,
      datasets: [
        {
          label: 'Ventas Reales',
          data: salesData,
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'rgba(59, 130, 246, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
        },
        {
          label: 'Meta por Hora',
          data: targetData,
          borderColor: 'rgba(156, 163, 175, 0.8)',
          backgroundColor: 'transparent',
          borderDash: [5, 5],
          tension: 0.4,
          pointRadius: 0,
        },
      ],
    };
  };

  const prepareBarChartData = () => {
    const labels = hourlyStatus.map(h => `${h.hour}h`);
    const percentages = hourlyStatus.map(h => h.adaptivePercentage || h.percentage);

    return {
      labels,
      datasets: [
        {
          label: 'Cumplimiento %',
          data: percentages,
          backgroundColor: percentages.map(p =>
            p >= 100 ? 'rgba(30, 64, 175, 0.9)' :
            p >= 80 ? 'rgba(59, 130, 246, 0.8)' :
            p >= 50 ? 'rgba(147, 197, 253, 0.8)' :
            'rgba(191, 219, 254, 0.8)'
          ),
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  };

  const prepareDoughnutData = () => {
    const ahead = summary?.hoursAhead || 0;
    const onTrack = summary?.hoursOnTrack || 0;
    const behind = summary?.hoursBehind || 0;

    return {
      labels: ['Adelantadas', 'En Meta', 'Atrasadas'],
      datasets: [
        {
          data: [ahead, onTrack, behind],
          backgroundColor: [
            'rgba(30, 64, 175, 0.9)',
            'rgba(59, 130, 246, 0.9)',
            'rgba(147, 197, 253, 0.9)',
          ],
          borderWidth: 0,
          cutout: '70%',
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        borderRadius: 8,
        titleFont: { size: 13, weight: 600 as const },
        bodyFont: { size: 12 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6b7280', font: { size: 11 } },
      },
      y: {
        grid: { color: 'rgba(229, 231, 235, 0.5)' },
        ticks: {
          color: '#6b7280',
          font: { size: 11 },
          callback: (value: number | string) => formatCompactCurrency(Number(value)),
        },
      },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        max: 150,
        ticks: {
          color: '#6b7280',
          font: { size: 11 },
          callback: (value: number | string) => `${value}%`,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        padding: 12,
        borderRadius: 8,
      },
    },
  };

  if (configLoading || salesLoading) {
    return (
      <div className="bhp-container">
        <div className="bhp-loading">
          <div className="bhp-spinner"></div>
          <p>Cargando datos...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="bhp-container">
        <div className="bhp-error">
          <p>No se pudo cargar la configuración</p>
          <button onClick={() => refetchConfig()} className="bhp-btn bhp-btn-primary">
            Reintentar
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  const currentHour = new Date().getHours();
  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const progressPercent = summary ? Math.min(summary.overallPercentage, 100) : 0;
  const actualPercent = summary?.overallPercentage || 0;
  const remaining = dailyTarget - (summary?.totalSales || 0);
  const exceeded = remaining < 0; // Si remaining es negativo, superamos la meta
  const exceedAmount = Math.abs(remaining); // Monto del excedente

  // Verificar si el día seleccionado es laborable
  const getDayConfig = (date: Date) => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayConfigs = [
      { name: 'Domingo', enabled: config.sunday },
      { name: 'Lunes', enabled: config.monday },
      { name: 'Martes', enabled: config.tuesday },
      { name: 'Miércoles', enabled: config.wednesday },
      { name: 'Jueves', enabled: config.thursday },
      { name: 'Viernes', enabled: config.friday },
      { name: 'Sábado', enabled: config.saturday },
    ];
    return dayConfigs[dayOfWeek];
  };
  const selectedDayConfig = getDayConfig(selectedDate);
  const isWorkingDay = selectedDayConfig.enabled;

  return (
    <div className="bhp-container">
      {/* Modern Header */}
      <div className="bhp-header">
        <div className="bhp-header-content">
          <div className="bhp-header-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </div>
          <div>
            <h1 className="bhp-title">Panel de Control Horario</h1>
            <p className="bhp-subtitle">Monitoreo en tiempo real del rendimiento de ventas</p>
          </div>
        </div>
        <button className="bhp-config-btn" onClick={() => setShowConfigModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Configurar Horarios
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="bhp-tabs">
        <button
          className={`bhp-tab ${activeTab === 'horarios' ? 'active' : ''}`}
          onClick={() => setActiveTab('horarios')}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Horarios Laborales
        </button>
        <button
          className={`bhp-tab ${activeTab === 'distribucion' ? 'active' : ''}`}
          onClick={() => setActiveTab('distribucion')}
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Distribución por Hora
        </button>
      </div>

      {/* Tab Content: Horarios Laborales */}
      {activeTab === 'horarios' && (
      <>
      {/* Quick Info Bar */}
      <div className="bhp-info-bar">
        <div className="bhp-info-item">
          <span className="bhp-info-label">Meta Diaria</span>
          <span className="bhp-info-value">{formatCurrency(dailyTarget)}</span>
        </div>
        <div className="bhp-info-divider"></div>
        <div className="bhp-info-item">
          <span className="bhp-info-label">Horario</span>
          <span className="bhp-info-value">{formatTime(config.startHour, config.startMinute)} - {formatTime(config.endHour, config.endMinute)}</span>
        </div>
        <div className="bhp-info-divider"></div>
        <div className="bhp-info-item">
          <span className="bhp-info-label">Meta/Hora</span>
          <span className="bhp-info-value">{formatCurrency(dailyTarget / config.totalHours)}</span>
        </div>
        <div className="bhp-info-divider"></div>
        <div className="bhp-info-item">
          <span className="bhp-info-label">Jornada</span>
          <span className="bhp-info-value">{config.totalHours} horas</span>
        </div>
        <div className="bhp-info-divider"></div>
        <div className="bhp-info-item">
          <span className="bhp-info-label">Días Laborables</span>
          <div className="bhp-days-mini">
            <span className={`bhp-day-mini ${config.monday ? 'active' : ''}`} title="Lunes">L</span>
            <span className={`bhp-day-mini ${config.tuesday ? 'active' : ''}`} title="Martes">M</span>
            <span className={`bhp-day-mini ${config.wednesday ? 'active' : ''}`} title="Miércoles">M</span>
            <span className={`bhp-day-mini ${config.thursday ? 'active' : ''}`} title="Jueves">J</span>
            <span className={`bhp-day-mini ${config.friday ? 'active' : ''}`} title="Viernes">V</span>
            <span className={`bhp-day-mini ${config.saturday ? 'active' : ''}`} title="Sábado">S</span>
            <span className={`bhp-day-mini ${config.sunday ? 'active' : ''}`} title="Domingo">D</span>
          </div>
        </div>
      </div>

      {/* Date Selector */}
      <div className="bhp-date-selector">
        <div className="bhp-date-input-wrapper">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00:00'))}
            className="bhp-date-input"
          />
        </div>
        {isToday && <span className="bhp-today-badge">HOY</span>}
        {!isWorkingDay && (
          <span className="bhp-non-working-badge">DÍA NO LABORABLE</span>
        )}
      </div>

      {/* Non-Working Day Warning */}
      {!isWorkingDay && (
        <div className="bhp-non-working-banner">
          <div className="bhp-non-working-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="bhp-non-working-content">
            <h3>{selectedDayConfig.name} no es día laborable</h3>
            <p>
              Este día no está configurado como día de trabajo. No se mostrará información de metas ni seguimiento horario.
            </p>
            <button
              className="bhp-btn bhp-btn-configure"
              onClick={() => setShowConfigModal(true)}
            >
              Configurar Semana Laboral
            </button>
          </div>
        </div>
      )}

      {/* Main Metrics Grid */}
      {summary && isWorkingDay && (
        <div className="bhp-metrics-grid">
          {/* Total Sales Card */}
          <div className="bhp-metric-card bhp-metric-primary">
            <div className="bhp-metric-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="bhp-metric-content">
              <span className="bhp-metric-label">Ventas del Día</span>
              <span className="bhp-metric-value">{formatCurrency(summary.totalSales)}</span>
              <span className="bhp-metric-detail">{summary.totalCount} VENTAS • {summary.overallPercentage.toFixed(1)}% DE LA META</span>
            </div>
          </div>

          {/* Target Per Hour Card */}
          <div className="bhp-metric-card bhp-metric-secondary">
            <div className="bhp-metric-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="12" r="2" fill="currentColor"/>
              </svg>
            </div>
            <div className="bhp-metric-content">
              <span className="bhp-metric-label">Meta por Hora</span>
              <span className="bhp-metric-value">{formatCurrency(summary.targetPerHour)}</span>
              <span className="bhp-metric-detail">{config.totalHours} HORAS DE TRABAJO</span>
            </div>
          </div>

          {/* Hours Status Card */}
          <div className="bhp-metric-card bhp-metric-tertiary">
            <div className="bhp-metric-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div className="bhp-metric-content">
              <span className="bhp-metric-label">Horas Registradas</span>
              <span className="bhp-metric-value">{summary.hoursCompleted}</span>
              <div className="bhp-hours-status">
                <span className="bhp-status-ahead">{summary.hoursAhead} adelantadas</span>
                <span className="bhp-status-track">{summary.hoursOnTrack} en meta</span>
                <span className="bhp-status-behind">{summary.hoursBehind} atrasadas</span>
              </div>
            </div>
          </div>

          {/* Progress Card */}
          <div className={`bhp-metric-card bhp-metric-progress ${exceeded ? 'bhp-metric-exceeded' : ''}`}>
            <div className="bhp-metric-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 20V10M12 20V4M6 20v-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="bhp-metric-content">
              <span className="bhp-metric-label">Progreso del Día</span>
              <div className="bhp-progress-bar-container">
                <div className="bhp-progress-bar">
                  <div
                    className={`bhp-progress-fill ${progressPercent >= 100 ? 'complete' : ''}`}
                    style={{ width: `${progressPercent}%` }}
                  >
                    <span className="bhp-progress-text">{actualPercent.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
              {exceeded ? (
                <span className="bhp-metric-detail bhp-exceeded-text">
                  EXCEDENTE: +{formatCurrency(exceedAmount)} SOBRE LA META
                </span>
              ) : (
                <span className="bhp-metric-detail">
                  FALTAN {formatCurrency(remaining)} PARA LA META
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {isWorkingDay && hourlyStatus.length > 0 && (
        <div className="bhp-charts-section">
          <div className="bhp-chart-row">
            {/* Line Chart - Sales Trend */}
            <div className="bhp-chart-card bhp-chart-large">
              <div className="bhp-chart-header">
                <h3>Tendencia de Ventas por Hora</h3>
                <div className="bhp-chart-legend">
                  <span className="bhp-legend-item">
                    <span className="bhp-legend-dot bhp-dot-primary"></span>
                    Ventas Reales
                  </span>
                  <span className="bhp-legend-item">
                    <span className="bhp-legend-dot bhp-dot-secondary"></span>
                    Meta
                  </span>
                </div>
              </div>
              <div className="bhp-chart-body">
                <Line data={prepareLineChartData()} options={chartOptions} />
              </div>
            </div>

            {/* Doughnut Chart - Hours Distribution */}
            <div className="bhp-chart-card bhp-chart-small">
              <div className="bhp-chart-header">
                <h3>Estado de Horas</h3>
              </div>
              <div className="bhp-chart-body bhp-doughnut-container">
                <Doughnut data={prepareDoughnutData()} options={doughnutOptions} />
                <div className="bhp-doughnut-center">
                  <span className="bhp-doughnut-value">{summary?.hoursCompleted || 0}</span>
                  <span className="bhp-doughnut-label">horas</span>
                </div>
              </div>
              <div className="bhp-doughnut-legend">
                <div className="bhp-doughnut-legend-item">
                  <span className="bhp-doughnut-dot" style={{ background: 'rgba(30, 64, 175, 0.9)' }}></span>
                  <span>Adelantadas ({summary?.hoursAhead || 0})</span>
                </div>
                <div className="bhp-doughnut-legend-item">
                  <span className="bhp-doughnut-dot" style={{ background: 'rgba(59, 130, 246, 0.9)' }}></span>
                  <span>En Meta ({summary?.hoursOnTrack || 0})</span>
                </div>
                <div className="bhp-doughnut-legend-item">
                  <span className="bhp-doughnut-dot" style={{ background: 'rgba(147, 197, 253, 0.9)' }}></span>
                  <span>Atrasadas ({summary?.hoursBehind || 0})</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bar Chart - Hourly Performance */}
          <div className="bhp-chart-card bhp-chart-full">
            <div className="bhp-chart-header">
              <h3>Cumplimiento por Hora (%)</h3>
              <div className="bhp-chart-legend">
                <span className="bhp-legend-item">
                  <span className="bhp-legend-bar" style={{ background: 'rgba(30, 64, 175, 0.9)' }}></span>
                  100%+
                </span>
                <span className="bhp-legend-item">
                  <span className="bhp-legend-bar" style={{ background: 'rgba(59, 130, 246, 0.8)' }}></span>
                  80-99%
                </span>
                <span className="bhp-legend-item">
                  <span className="bhp-legend-bar" style={{ background: 'rgba(147, 197, 253, 0.8)' }}></span>
                  50-79%
                </span>
                <span className="bhp-legend-item">
                  <span className="bhp-legend-bar" style={{ background: 'rgba(191, 219, 254, 0.8)' }}></span>
                  &lt;50%
                </span>
              </div>
            </div>
            <div className="bhp-chart-body bhp-bar-chart">
              <Bar data={prepareBarChartData()} options={barChartOptions} />
            </div>
          </div>
        </div>
      )}

      {/* Detalle por Hora - Tabla Unificada */}
      {isWorkingDay && (
      <div className="bhp-table-section bhp-unified-table">
        <div className="bhp-table-header">
          <h3>Detalle por Hora</h3>
        </div>

        {statusLoading ? (
          <div className="bhp-table-loading">Cargando datos...</div>
        ) : hourlyStatus.length === 0 ? (
          <div className="bhp-table-empty">
            <p>No hay datos disponibles para esta fecha</p>
          </div>
        ) : (
          <div className="bhp-table-container">
            <table className="bhp-table bhp-table-unified">
              <thead>
                {/* Fila de grupos */}
                <tr className="bhp-header-groups">
                  <th rowSpan={2} className="bhp-th-hora">HORA</th>
                  <th colSpan={4} className="bhp-th-pos">MODO ESPECIAL POS (MONTO DIARIO)</th>
                  <th colSpan={4} className="bhp-th-electronic">CONTROL POR IMPUESTO (ELECTRÓNICAS)</th>
                </tr>
                {/* Fila de columnas */}
                <tr className="bhp-header-columns">
                  {/* POS */}
                  <th className="bhp-th-pos-sub">META</th>
                  <th className="bhp-th-pos-sub">VENTA</th>
                  <th className="bhp-th-pos-sub">%</th>
                  <th className="bhp-th-pos-sub">ESTADO</th>
                  {/* Electrónica */}
                  <th className="bhp-th-elec-sub">META IMP.</th>
                  <th className="bhp-th-elec-sub">IMPUESTOS</th>
                  <th className="bhp-th-elec-sub">%</th>
                  <th className="bhp-th-elec-sub">ESTADO</th>
                </tr>
              </thead>
              <tbody>
                {hourlyStatus.map((hourStatus) => {
                  const isPast = hourStatus.isPastHour ?? (isToday && hourStatus.hour < currentHour);
                  const isCurrent = hourStatus.isCurrentHour ?? (isToday && hourStatus.hour === currentHour);
                  const isFuture = !isPast && !isCurrent;

                  // Estado POS
                  const posStatus = getStatusColor(hourStatus);

                  // Estado Electrónico
                  const getElectronicStatus = () => {
                    if (hourStatus.electronicIsAhead) return 'ahead';
                    if (hourStatus.electronicIsOnTrack) return 'on-track';
                    return 'behind';
                  };
                  const electronicStatus = getElectronicStatus();

                  // Mostrar meta adaptativa para TODAS las horas (el saldo acumulado
                  // se arrastra secuencialmente desde la primera hora)
                  const showAdaptive = true;
                  const displayPosTarget = hourStatus.adaptiveTargetPerHour ?? hourStatus.targetPerHour;
                  const displayPosPercent = hourStatus.adaptivePercentage ?? hourStatus.percentage;

                  const displayElecTarget = hourStatus.electronicAdaptiveTarget ?? hourStatus.electronicTargetPerHour ?? 0;
                  const displayElecPercent = hourStatus.electronicAdaptivePercentage ?? hourStatus.electronicPercentage ?? 0;

                  return (
                    <tr key={hourStatus.hour} className={`${isCurrent ? 'bhp-row-current' : ''} ${isPast ? 'bhp-row-past' : ''} ${isFuture ? 'bhp-row-future' : ''}`}>
                      {/* Hora */}
                      <td className="bhp-cell-hora">
                        <div className="bhp-hora-content">
                          <span className="bhp-hora-time">{formatTime(hourStatus.hour, 0)}</span>
                          {isCurrent && <span className="bhp-badge-now">AHORA</span>}
                        </div>
                      </td>

                      {/* === MODO ESPECIAL POS === */}
                      <td className="bhp-cell-pos">
                        <div className="bhp-meta-cell">
                          <span className="bhp-meta-value">{formatCurrency(displayPosTarget)}</span>
                          {showAdaptive && hourStatus.adaptiveTargetPerHour !== hourStatus.targetPerHour && (
                            <span className="bhp-meta-original">Orig: {formatCurrency(hourStatus.targetPerHour)}</span>
                          )}
                        </div>
                      </td>
                      <td className="bhp-cell-pos">
                        <div className="bhp-venta-cell">
                          <span className="bhp-venta-value">{formatCurrency(hourStatus.salesAmount)}</span>
                          <span className="bhp-venta-count">({hourStatus.salesCount})</span>
                        </div>
                      </td>
                      <td className="bhp-cell-pos bhp-cell-percent">
                        <span className={`bhp-percent-badge bhp-percent-${posStatus}`}>
                          {displayPosPercent.toFixed(1)}%
                        </span>
                      </td>
                      <td className="bhp-cell-pos bhp-cell-status">
                        <span className={`bhp-status-badge bhp-status-${posStatus}`}>
                          <span className="bhp-status-text">
                            {posStatus === 'ahead' && 'Adelantado'}
                            {posStatus === 'on-track' && 'En Meta'}
                            {posStatus === 'behind' && 'Atrasado'}
                          </span>
                        </span>
                      </td>

                      {/* === CONTROL POR IMPUESTO (ELECTRÓNICAS) === */}
                      <td className="bhp-cell-elec">
                        <div className="bhp-meta-cell">
                          <span className="bhp-meta-value">{formatCurrency(displayElecTarget)}</span>
                          {showAdaptive && hourStatus.electronicAdaptiveTarget !== hourStatus.electronicTargetPerHour && (
                            <span className="bhp-meta-original">Orig: {formatCurrency(hourStatus.electronicTargetPerHour || 0)}</span>
                          )}
                        </div>
                      </td>
                      <td className="bhp-cell-elec">
                        <div className="bhp-venta-cell">
                          <span className="bhp-venta-value">{formatCurrency(hourStatus.electronicTaxAmount || 0)}</span>
                          <span className="bhp-venta-count">({hourStatus.electronicSalesCount || 0})</span>
                        </div>
                      </td>
                      <td className="bhp-cell-elec bhp-cell-percent">
                        <span className={`bhp-percent-badge bhp-percent-${electronicStatus}`}>
                          {displayElecPercent.toFixed(1)}%
                        </span>
                      </td>
                      <td className="bhp-cell-elec bhp-cell-status">
                        <span className={`bhp-status-badge bhp-status-${electronicStatus}`}>
                          <span className="bhp-status-text">
                            {electronicStatus === 'ahead' && 'Adelantado'}
                            {electronicStatus === 'on-track' && 'En Meta'}
                            {electronicStatus === 'behind' && 'Atrasado'}
                          </span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}
      </>
      )}

      {/* Tab Content: Distribución por Hora */}
      {activeTab === 'distribucion' && (
      <div className="bhp-distribution-content">
        {/* Distribution Header */}
        <div className="bhp-dist-header">
          <div className="bhp-dist-header-info">
            <h2 className="bhp-dist-title">Distribución de Porcentajes por Hora</h2>
            <p className="bhp-dist-subtitle">Configura el porcentaje del objetivo para cada franja horaria</p>
          </div>
          <button className="bhp-btn bhp-btn-danger-outline" onClick={handleResetAll} disabled={resetting}>
            {resetting ? 'Reseteando...' : 'Resetear Todo'}
          </button>
        </div>

        {/* Days Summary Bar */}
        <div className="bhp-days-bar">
          {dayOfWeekOrder.map((day) => {
            const daySummary = distSummary.find(s => s.dayOfWeek === day);
            const isSelected = day === selectedDay;
            const isComplete = daySummary?.isComplete || false;
            const percentage = daySummary?.totalPercentage || 0;

            return (
              <button
                key={day}
                className={`bhp-day-btn ${isSelected ? 'selected' : ''} ${isComplete ? 'complete' : 'incomplete'}`}
                onClick={() => setSelectedDay(day)}
              >
                <span className="bhp-day-name">{dayOfWeekShortLabels[day]}</span>
                <span className="bhp-day-percent">{percentage.toFixed(0)}%</span>
                <span className={`bhp-day-status ${isComplete ? 'complete' : ''}`}></span>
              </button>
            );
          })}
        </div>

        {/* Selected Day Header */}
        <div className="bhp-selected-day">
          <div className="bhp-selected-day-info">
            <h3 className="bhp-selected-day-title">{dayOfWeekLabels[selectedDay]}</h3>
            <div className="bhp-selected-day-stats">
              <span className={`bhp-stat-badge ${currentDayDistSummary?.isComplete ? 'success' : 'warning'}`}>
                {currentDayDistSummary?.totalPercentage.toFixed(1) || 0}% asignado
              </span>
              <span className="bhp-stat-badge">{currentDayDistSummary?.activeDistributions || 0} distribuciones</span>
            </div>
          </div>
          <div className="bhp-selected-day-actions">
            <button className="bhp-btn bhp-btn-primary" onClick={() => setShowDistCreateModal(true)}>
              Nueva
            </button>
            <button className="bhp-btn bhp-btn-secondary" onClick={() => setShowDistributeModal(true)}>
              Distribuir Uniforme
            </button>
            <button className="bhp-btn bhp-btn-secondary" onClick={() => setShowCopyModal(true)} disabled={distributions.length === 0}>
              Copiar a Otros Días
            </button>
            {distributions.length > 0 && (
              <button className="bhp-btn bhp-btn-danger-outline" onClick={handleDeleteByDay} disabled={deletingByDay}>
                Limpiar Día
              </button>
            )}
          </div>
        </div>

        {/* Alert for incomplete distribution */}
        {distAlert && distAlert.hasAlert && (
          <div className="bhp-dist-alert">
            <div className="bhp-dist-alert-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="bhp-dist-alert-content">
              <strong>Porcentaje sin asignar:</strong> {distAlert.unassignedPercentage.toFixed(2)}%
              <span className="bhp-dist-alert-detail">Total asignado: {distAlert.totalAssigned.toFixed(2)}%</span>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        <div className="bhp-dist-progress">
          <div className="bhp-dist-progress-header">
            <span>Progreso del día</span>
            <span className={currentDayDistSummary?.isComplete ? 'complete' : ''}>
              {currentDayDistSummary?.totalPercentage.toFixed(1) || 0}% / 100%
            </span>
          </div>
          <div className="bhp-dist-progress-bar">
            <div
              className={`bhp-dist-progress-fill ${(currentDayDistSummary?.totalPercentage || 0) >= 100 ? 'complete' : ''}`}
              style={{ width: `${Math.min(currentDayDistSummary?.totalPercentage || 0, 100)}%` }}
            />
          </div>
        </div>

        {/* Distributions Table */}
        <div className="bhp-dist-table-section">
          {loadingDistributions ? (
            <div className="bhp-dist-loading">Cargando distribuciones...</div>
          ) : distributions.length === 0 ? (
            <div className="bhp-dist-empty">
              <p>No hay distribuciones para {dayOfWeekLabels[selectedDay]}</p>
              <div className="bhp-dist-empty-actions">
                <button className="bhp-btn bhp-btn-primary" onClick={() => setShowDistCreateModal(true)}>
                  Crear Distribución
                </button>
                <button className="bhp-btn bhp-btn-secondary" onClick={() => setShowDistributeModal(true)}>
                  Distribuir Uniforme
                </button>
              </div>
            </div>
          ) : (
            <div className="bhp-dist-table-container">
              <table className="bhp-dist-table">
                <thead>
                  <tr>
                    <th>RANGO HORARIO</th>
                    <th>PORCENTAJE</th>
                    <th>DESCRIPCIÓN</th>
                    <th>ESTADO</th>
                    <th>ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {distributions.map((dist) => (
                    <tr key={dist.id} className={!dist.isActive ? 'inactive' : ''}>
                      <td className="bhp-dist-time">
                        {formatDistributionTime(dist.startHour, dist.startMinute)} - {formatDistributionTime(dist.endHour, dist.endMinute)}
                      </td>
                      <td>
                        <span className="bhp-dist-percent-badge">{dist.percentage.toFixed(1)}%</span>
                      </td>
                      <td className="bhp-dist-desc">{dist.description || '-'}</td>
                      <td>
                        <button
                          className={`bhp-dist-toggle ${dist.isActive ? 'active' : ''}`}
                          onClick={() => handleToggleDistActive(dist)}
                          disabled={updatingDist}
                        >
                          {dist.isActive ? 'Activa' : 'Inactiva'}
                        </button>
                      </td>
                      <td>
                        <div className="bhp-dist-actions">
                          <button className="bhp-dist-action-btn" onClick={() => setEditingDist(dist)} title="Editar">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                          <button className="bhp-dist-action-btn danger" onClick={() => handleDeleteDist(dist.id)} disabled={deletingDist} title="Eliminar">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Create/Edit Distribution Modal */}
      {(showDistCreateModal || editingDist) && (
        <div className="bhp-modal-overlay" onClick={closeDistModal}>
          <div className="bhp-modal bhp-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="bhp-modal-header">
              <h3>{editingDist ? 'Editar Distribución' : `Nueva Distribución - ${dayOfWeekLabels[selectedDay]}`}</h3>
              <button className="bhp-modal-close" onClick={closeDistModal}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="bhp-modal-body">
              <div className="bhp-config-section">
                <h4>Rango Horario</h4>
                <div className="bhp-time-grid">
                  <div className="bhp-time-group">
                    <label>Hora de Inicio</label>
                    <div className="bhp-time-inputs">
                      <select value={distFormData.startHour} onChange={(e) => setDistFormData({ ...distFormData, startHour: parseInt(e.target.value) })}>
                        {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatDistributionTime(i, 0)}</option>)}
                      </select>
                      <select value={distFormData.startMinute} onChange={(e) => setDistFormData({ ...distFormData, startMinute: parseInt(e.target.value) })}>
                        {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="bhp-time-group">
                    <label>Hora de Fin</label>
                    <div className="bhp-time-inputs">
                      <select value={distFormData.endHour} onChange={(e) => setDistFormData({ ...distFormData, endHour: parseInt(e.target.value) })}>
                        {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatDistributionTime(i, 0)}</option>)}
                      </select>
                      <select value={distFormData.endMinute} onChange={(e) => setDistFormData({ ...distFormData, endMinute: parseInt(e.target.value) })}>
                        {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bhp-config-section">
                <h4>Porcentaje del Objetivo</h4>
                <div className="bhp-percentage-input">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={distFormData.percentage}
                    onChange={(e) => setDistFormData({ ...distFormData, percentage: parseFloat(e.target.value) || 0 })}
                  />
                  <span>%</span>
                </div>
                <p className="bhp-help-text">
                  Asignado: {currentDayDistSummary?.totalPercentage.toFixed(1) || 0}% |
                  Disponible: {(100 - (currentDayDistSummary?.totalPercentage || 0)).toFixed(1)}%
                </p>
              </div>
              <div className="bhp-config-section">
                <h4>Descripción (Opcional)</h4>
                <input
                  type="text"
                  className="bhp-input"
                  value={distFormData.description}
                  onChange={(e) => setDistFormData({ ...distFormData, description: e.target.value })}
                  placeholder="Ej: Hora pico almuerzo"
                />
              </div>
            </div>
            <div className="bhp-modal-footer">
              <button className="bhp-btn bhp-btn-secondary" onClick={closeDistModal} disabled={creatingDist || updatingDist}>
                Cancelar
              </button>
              <button
                className="bhp-btn bhp-btn-primary"
                onClick={editingDist ? handleUpdateDist : handleCreateDist}
                disabled={creatingDist || updatingDist}
              >
                {creatingDist || updatingDist ? 'Guardando...' : editingDist ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Distribute Uniformly Modal */}
      {showDistributeModal && (
        <div className="bhp-modal-overlay" onClick={() => setShowDistributeModal(false)}>
          <div className="bhp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bhp-modal-header">
              <h3>Distribuir Uniforme - {dayOfWeekLabels[selectedDay]}</h3>
              <button className="bhp-modal-close" onClick={() => setShowDistributeModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="bhp-modal-body">
              <div className="bhp-dist-info-box">
                <p>Se crearán distribuciones automáticas para cada hora, distribuyendo el 100% uniformemente.</p>
              </div>
              <div className="bhp-config-section">
                <h4>Horario Laboral</h4>
                <div className="bhp-time-grid">
                  <div className="bhp-time-group">
                    <label>Hora de Inicio</label>
                    <select value={distributeConfig.businessStartHour} onChange={(e) => setDistributeConfig({ ...distributeConfig, businessStartHour: parseInt(e.target.value) })}>
                      {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatDistributionTime(i, 0)}</option>)}
                    </select>
                  </div>
                  <div className="bhp-time-group">
                    <label>Hora de Fin</label>
                    <select value={distributeConfig.businessEndHour} onChange={(e) => setDistributeConfig({ ...distributeConfig, businessEndHour: parseInt(e.target.value) })}>
                      {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{formatDistributionTime(i, 0)}</option>)}
                    </select>
                  </div>
                </div>
                <p className="bhp-help-text">
                  Se crearán {distributeConfig.businessEndHour - distributeConfig.businessStartHour} distribuciones de {(100 / (distributeConfig.businessEndHour - distributeConfig.businessStartHour)).toFixed(2)}% cada una
                </p>
              </div>
              <div className="bhp-config-section">
                <label className="bhp-checkbox-label">
                  <input
                    type="checkbox"
                    checked={distributeConfig.clearExisting}
                    onChange={(e) => setDistributeConfig({ ...distributeConfig, clearExisting: e.target.checked })}
                  />
                  <span>Eliminar distribuciones existentes</span>
                </label>
              </div>
            </div>
            <div className="bhp-modal-footer">
              <button className="bhp-btn bhp-btn-secondary" onClick={() => setShowDistributeModal(false)} disabled={distributing}>
                Cancelar
              </button>
              <button className="bhp-btn bhp-btn-primary" onClick={handleDistributeUniformly} disabled={distributing}>
                {distributing ? 'Distribuyendo...' : 'Distribuir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Distributions Modal */}
      {showCopyModal && (
        <div className="bhp-modal-overlay" onClick={() => setShowCopyModal(false)}>
          <div className="bhp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bhp-modal-header">
              <h3>Copiar desde {dayOfWeekLabels[selectedDay]}</h3>
              <button className="bhp-modal-close" onClick={() => setShowCopyModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="bhp-modal-body">
              <div className="bhp-dist-info-box">
                <p>Las {distributions.length} distribuciones se copiarán a los días seleccionados.</p>
              </div>
              <div className="bhp-config-section">
                <h4>Días de destino</h4>
                <div className="bhp-copy-days-grid">
                  {dayOfWeekOrder.filter(d => d !== selectedDay).map((day) => {
                    const daySummary = distSummary.find(s => s.dayOfWeek === day);
                    const isChecked = copyConfig.toDays.includes(day);
                    return (
                      <label key={day} className={`bhp-copy-day ${isChecked ? 'selected' : ''}`}>
                        <input type="checkbox" checked={isChecked} onChange={() => toggleCopyDay(day)} />
                        <div className="bhp-copy-day-info">
                          <span className="bhp-copy-day-name">{dayOfWeekLabels[day]}</span>
                          <span className="bhp-copy-day-stat">{daySummary?.activeDistributions || 0} dist.</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="bhp-config-section">
                <label className="bhp-checkbox-label warning">
                  <input
                    type="checkbox"
                    checked={copyConfig.overwrite}
                    onChange={(e) => setCopyConfig({ ...copyConfig, overwrite: e.target.checked })}
                  />
                  <span>Sobrescribir distribuciones existentes</span>
                </label>
              </div>
            </div>
            <div className="bhp-modal-footer">
              <button className="bhp-btn bhp-btn-secondary" onClick={() => { setShowCopyModal(false); setCopyConfig({ toDays: [], overwrite: false }); }} disabled={copying}>
                Cancelar
              </button>
              <button className="bhp-btn bhp-btn-primary" onClick={handleCopyDist} disabled={copying || copyConfig.toDays.length === 0}>
                {copying ? 'Copiando...' : `Copiar a ${copyConfig.toDays.length} días`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="bhp-modal-overlay" onClick={() => setShowConfigModal(false)}>
          <div className="bhp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bhp-modal-header">
              <h3>Configurar Horarios Laborales</h3>
              <button className="bhp-modal-close" onClick={() => setShowConfigModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="bhp-modal-body">
              <div className="bhp-config-section">
                <h4>Horario de Trabajo</h4>
                <div className="bhp-time-grid">
                  <div className="bhp-time-group">
                    <label>Hora de Inicio</label>
                    <select
                      value={formData.startHour}
                      onChange={(e) => setFormData({ ...formData, startHour: parseInt(e.target.value) })}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{formatTime(i, 0)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bhp-time-group">
                    <label>Hora de Cierre</label>
                    <select
                      value={formData.endHour}
                      onChange={(e) => setFormData({ ...formData, endHour: parseInt(e.target.value) })}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{formatTime(i, 0)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="bhp-config-section">
                <h4>Semana Laboral</h4>
                <p className="bhp-config-description">
                  Selecciona los días en que el negocio está abierto. Los días no seleccionados se mostrarán como "No laborables".
                </p>
                <div className="bhp-days-grid">
                  {[
                    { key: 'monday', label: 'Lunes', short: 'L' },
                    { key: 'tuesday', label: 'Martes', short: 'M' },
                    { key: 'wednesday', label: 'Miércoles', short: 'M' },
                    { key: 'thursday', label: 'Jueves', short: 'J' },
                    { key: 'friday', label: 'Viernes', short: 'V' },
                    { key: 'saturday', label: 'Sábado', short: 'S' },
                    { key: 'sunday', label: 'Domingo', short: 'D' },
                  ].map(({ key, label, short }) => (
                    <label key={key} className={`bhp-day-toggle ${formData[key as keyof typeof formData] ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={formData[key as keyof typeof formData] as boolean}
                        onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                      />
                      <span className="bhp-day-short">{short}</span>
                      <span className="bhp-day-full">{label}</span>
                    </label>
                  ))}
                </div>
                <div className="bhp-days-summary">
                  <span className="bhp-days-count">
                    {Object.entries(formData)
                      .filter(([key]) => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(key))
                      .filter(([, value]) => value === true)
                      .length} días laborables por semana
                  </span>
                </div>
              </div>
            </div>
            <div className="bhp-modal-footer">
              <button className="bhp-btn bhp-btn-secondary" onClick={() => setShowConfigModal(false)}>
                Cancelar
              </button>
              <button className="bhp-btn bhp-btn-primary" onClick={handleSaveConfig} disabled={updating}>
                {updating ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .bhp-container {
    padding: 0;
    background: var(--bg-secondary, #f8fafc);
    min-height: 100vh;
  }

  /* Tabs Navigation */
  .bhp-tabs {
    display: flex;
    gap: 8px;
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .bhp-tab {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-secondary, #64748b);
    cursor: pointer;
    transition: all 0.15s;
  }

  .bhp-tab svg {
    width: 16px;
    height: 16px;
  }

  .bhp-tab:hover {
    background: var(--bg-secondary, #f1f5f9);
    border-color: #3b82f6;
  }

  .bhp-tab.active {
    background: #3b82f6;
    color: white;
    border-color: #2563eb;
  }

  /* Distribution Content */
  .bhp-distribution-content {
    padding: 0 16px;
  }

  .bhp-dist-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-left: 4px solid #3b82f6;
    padding: 16px 20px;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .bhp-dist-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    margin: 0;
  }

  .bhp-dist-subtitle {
    font-size: 0.75rem;
    color: var(--text-secondary, #64748b);
    margin: 4px 0 0 0;
  }

  .bhp-btn-danger-outline {
    background: transparent;
    color: #64748b;
    border: 1px solid var(--border-color, #e2e8f0);
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .bhp-btn-danger-outline:hover {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #dc2626;
  }

  /* Days Bar */
  .bhp-days-bar {
    display: flex;
    gap: 8px;
    padding: 12px 16px;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 8px;
    margin-bottom: 16px;
    overflow-x: auto;
  }

  .bhp-day-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 10px 14px;
    background: var(--bg-secondary, #f1f5f9);
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s;
    min-width: 60px;
    position: relative;
  }

  .bhp-day-btn:hover {
    border-color: #3b82f6;
  }

  .bhp-day-btn.selected {
    background: #3b82f6;
    color: white;
  }

  .bhp-day-btn.complete .bhp-day-status {
    background: #1e40af;
  }

  .bhp-day-btn.incomplete .bhp-day-status {
    background: #94a3b8;
  }

  .bhp-day-name {
    font-size: 0.8rem;
    font-weight: 600;
  }

  .bhp-day-percent {
    font-size: 0.7rem;
    opacity: 0.8;
  }

  .bhp-day-status {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid var(--bg-primary, #ffffff);
  }

  /* Selected Day Header */
  .bhp-selected-day {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 14px 16px;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-left: 4px solid #60a5fa;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .bhp-selected-day-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    margin: 0 0 6px 0;
  }

  .bhp-selected-day-stats {
    display: flex;
    gap: 8px;
  }

  .bhp-stat-badge {
    font-size: 0.7rem;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 4px;
    background: var(--bg-secondary, #f1f5f9);
    color: var(--text-secondary, #64748b);
  }

  .bhp-stat-badge.success {
    background: #dbeafe;
    color: #1e40af;
  }

  .bhp-stat-badge.warning {
    background: #fef3c7;
    color: #92400e;
  }

  .bhp-selected-day-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  /* Distribution Alert */
  .bhp-dist-alert {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .bhp-dist-alert-icon {
    color: #d97706;
    flex-shrink: 0;
  }

  .bhp-dist-alert-icon svg {
    width: 20px;
    height: 20px;
  }

  .bhp-dist-alert-content {
    font-size: 0.8rem;
    color: #92400e;
  }

  .bhp-dist-alert-detail {
    margin-left: 8px;
    opacity: 0.8;
  }

  /* Distribution Progress */
  .bhp-dist-progress {
    padding: 14px 16px;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 8px;
    margin-bottom: 16px;
  }

  .bhp-dist-progress-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.8rem;
    margin-bottom: 8px;
  }

  .bhp-dist-progress-header .complete {
    color: #1e40af;
    font-weight: 600;
  }

  .bhp-dist-progress-bar {
    height: 10px;
    background: var(--border-color, #e2e8f0);
    border-radius: 5px;
    overflow: hidden;
  }

  .bhp-dist-progress-fill {
    height: 100%;
    background: #60a5fa;
    border-radius: 5px;
    transition: width 0.3s;
  }

  .bhp-dist-progress-fill.complete {
    background: #1e40af;
  }

  /* Distribution Table */
  .bhp-dist-table-section {
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 8px;
    overflow: hidden;
  }

  .bhp-dist-table-container {
    overflow-x: auto;
  }

  .bhp-dist-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
  }

  .bhp-dist-table thead {
    background: var(--bg-secondary, #f8fafc);
  }

  .bhp-dist-table th {
    padding: 10px 12px;
    text-align: left;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-secondary, #64748b);
    border-bottom: 1px solid var(--border-color, #e2e8f0);
  }

  .bhp-dist-table td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border-color, #f1f5f9);
    vertical-align: middle;
  }

  .bhp-dist-table tbody tr:last-child td {
    border-bottom: none;
  }

  .bhp-dist-table tbody tr:hover {
    background: var(--bg-secondary, #f8fafc);
  }

  .bhp-dist-table tbody tr.inactive {
    opacity: 0.5;
  }

  .bhp-dist-time {
    font-weight: 600;
    color: var(--text-primary, #1e293b);
  }

  .bhp-dist-percent-badge {
    display: inline-block;
    padding: 3px 8px;
    background: #dbeafe;
    color: #1e40af;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.75rem;
  }

  .bhp-dist-desc {
    color: var(--text-secondary, #64748b);
  }

  .bhp-dist-toggle {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid var(--border-color, #e2e8f0);
    background: var(--bg-secondary, #f1f5f9);
    color: var(--text-secondary, #64748b);
  }

  .bhp-dist-toggle.active {
    background: #dbeafe;
    color: #1e40af;
    border-color: #93c5fd;
  }

  .bhp-dist-actions {
    display: flex;
    gap: 6px;
  }

  .bhp-dist-action-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-secondary, #f1f5f9);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    color: var(--text-secondary, #64748b);
  }

  .bhp-dist-action-btn svg {
    width: 14px;
    height: 14px;
  }

  .bhp-dist-action-btn:hover {
    background: #dbeafe;
    color: #1e40af;
    border-color: #93c5fd;
  }

  .bhp-dist-action-btn.danger:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fca5a5;
  }

  .bhp-dist-loading,
  .bhp-dist-empty {
    padding: 40px;
    text-align: center;
    color: var(--text-secondary, #64748b);
    font-size: 0.85rem;
  }

  .bhp-dist-empty-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 16px;
  }

  /* Distribution Info Box */
  .bhp-dist-info-box {
    padding: 12px;
    background: #dbeafe;
    border-radius: 6px;
    margin-bottom: 16px;
  }

  .bhp-dist-info-box p {
    margin: 0;
    font-size: 0.8rem;
    color: #1e40af;
  }

  /* Time Inputs */
  .bhp-time-inputs {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .bhp-time-inputs select {
    padding: 8px 10px;
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    font-size: 0.8rem;
    background: var(--bg-primary, #ffffff);
    cursor: pointer;
  }

  /* Percentage Input */
  .bhp-percentage-input {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .bhp-percentage-input input {
    width: 100px;
    padding: 8px 12px;
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .bhp-percentage-input span {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-secondary, #64748b);
  }

  .bhp-help-text {
    font-size: 0.75rem;
    color: var(--text-secondary, #94a3b8);
    margin-top: 6px;
  }

  .bhp-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    font-size: 0.85rem;
  }

  .bhp-input:focus {
    outline: none;
    border-color: #3b82f6;
  }

  /* Checkbox Label */
  .bhp-checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    font-size: 0.85rem;
  }

  .bhp-checkbox-label:hover {
    background: var(--bg-secondary, #f1f5f9);
  }

  .bhp-checkbox-label.warning {
    background: #fef3c7;
  }

  .bhp-checkbox-label input {
    width: 16px;
    height: 16px;
  }

  /* Copy Days Grid */
  .bhp-copy-days-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .bhp-copy-day {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: 2px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s;
  }

  .bhp-copy-day:hover {
    border-color: #3b82f6;
  }

  .bhp-copy-day.selected {
    border-color: #3b82f6;
    background: #dbeafe;
  }

  .bhp-copy-day input {
    width: 16px;
    height: 16px;
  }

  .bhp-copy-day-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .bhp-copy-day-name {
    font-size: 0.85rem;
    font-weight: 600;
  }

  .bhp-copy-day-stat {
    font-size: 0.7rem;
    color: var(--text-secondary, #64748b);
  }

  .bhp-modal-large {
    max-width: 500px;
  }

  /* Header - Minimalist */
  .bhp-header {
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-left: 4px solid #3b82f6;
    padding: 16px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 8px;
    margin: 0 16px 16px;
  }

  .bhp-header-content {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .bhp-header-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3b82f6;
  }

  .bhp-header-icon svg {
    width: 24px;
    height: 24px;
  }

  .bhp-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    margin: 0;
  }

  .bhp-subtitle {
    font-size: 0.8rem;
    color: var(--text-secondary, #64748b);
    margin: 2px 0 0 0;
  }

  .bhp-config-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    background: var(--bg-secondary, #f1f5f9);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    color: var(--text-primary, #1e293b);
    font-weight: 500;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .bhp-config-btn:hover {
    background: var(--border-color, #e2e8f0);
  }

  .bhp-config-btn svg {
    width: 16px;
    height: 16px;
    color: #3b82f6;
  }

  /* Info Bar - Compact */
  .bhp-info-bar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-left: 4px solid #60a5fa;
    padding: 12px 16px;
    margin: 0 16px 16px;
    border-radius: 8px;
    flex-wrap: wrap;
  }

  .bhp-info-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .bhp-info-label {
    font-size: 0.65rem;
    font-weight: 600;
    color: var(--text-secondary, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .bhp-info-value {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
  }

  .bhp-info-divider {
    width: 1px;
    height: 28px;
    background: var(--border-color, #e2e8f0);
  }

  /* Mini days indicators */
  .bhp-days-mini {
    display: flex;
    gap: 3px;
  }

  .bhp-day-mini {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
    font-weight: 600;
    background: var(--bg-secondary, #f1f5f9);
    color: var(--text-secondary, #94a3b8);
  }

  .bhp-day-mini.active {
    background: #3b82f6;
    color: white;
  }

  /* Date Selector - Compact */
  .bhp-date-selector {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .bhp-date-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    padding: 8px 12px;
    border-radius: 6px;
  }

  .bhp-date-input-wrapper svg {
    width: 16px;
    height: 16px;
    color: #3b82f6;
  }

  .bhp-date-input {
    border: none;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-primary, #1e293b);
    background: transparent;
    cursor: pointer;
  }

  .bhp-today-badge {
    background: #3b82f6;
    color: white;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .bhp-non-working-badge {
    background: #64748b;
    color: white;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  /* Non-Working Day Banner - Compact */
  .bhp-non-working-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-left: 4px solid #64748b;
    padding: 12px 16px;
    margin: 0 16px 16px;
    border-radius: 8px;
  }

  .bhp-non-working-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #64748b;
  }

  .bhp-non-working-icon svg {
    width: 20px;
    height: 20px;
  }

  .bhp-non-working-content {
    flex: 1;
  }

  .bhp-non-working-content h3 {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-primary, #334155);
    margin: 0 0 4px 0;
  }

  .bhp-non-working-content p {
    font-size: 0.75rem;
    color: var(--text-secondary, #64748b);
    margin: 0;
    line-height: 1.4;
  }

  .bhp-btn-configure {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
  }

  .bhp-btn-configure:hover {
    background: #2563eb;
  }

  /* Metrics Grid - Compact */
  .bhp-metrics-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .bhp-metric-card {
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-left: 4px solid #3b82f6;
  }

  .bhp-metric-card:hover {
    border-color: #3b82f6;
  }

  .bhp-metric-icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3b82f6;
  }

  .bhp-metric-icon svg {
    width: 20px;
    height: 20px;
  }

  .bhp-metric-primary .bhp-metric-icon { color: #2563eb; }
  .bhp-metric-secondary .bhp-metric-icon { color: #0369a1; }
  .bhp-metric-tertiary .bhp-metric-icon { color: #1d4ed8; }
  .bhp-metric-progress .bhp-metric-icon { color: #3b82f6; }

  .bhp-metric-primary { border-left-color: #2563eb; }
  .bhp-metric-secondary { border-left-color: #0ea5e9; }
  .bhp-metric-tertiary { border-left-color: #60a5fa; }
  .bhp-metric-progress { border-left-color: #93c5fd; }

  /* Estilos para cuando se supera la meta */
  .bhp-metric-exceeded {
    border-left-color: #1e40af;
    background: #eff6ff;
  }

  .bhp-metric-exceeded .bhp-metric-icon {
    color: #1e40af !important;
  }

  .bhp-exceeded-text {
    color: #1e40af !important;
    font-weight: 600 !important;
  }

  .bhp-metric-content {
    flex: 1;
    min-width: 0;
  }

  .bhp-metric-label {
    font-size: 0.65rem;
    font-weight: 600;
    color: var(--text-secondary, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: block;
    margin-bottom: 2px;
  }

  .bhp-metric-value {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--text-primary, #1e293b);
    display: block;
  }

  .bhp-metric-detail {
    font-size: 0.65rem;
    color: var(--text-secondary, #94a3b8);
    font-weight: 500;
    margin-top: 4px;
    display: block;
  }

  .bhp-hours-status {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
  }

  .bhp-status-ahead,
  .bhp-status-track,
  .bhp-status-behind {
    font-size: 0.6rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .bhp-status-ahead {
    background: #1e40af;
    color: white;
  }

  .bhp-status-track {
    background: #dbeafe;
    color: #1e40af;
  }

  .bhp-status-behind {
    background: #bfdbfe;
    color: #1e3a8a;
  }

  /* Progress Bar - Compact */
  .bhp-progress-bar-container {
    margin-top: 6px;
  }

  .bhp-progress-bar {
    height: 20px;
    background: var(--border-color, #e2e8f0);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  .bhp-progress-fill {
    height: 100%;
    background: #3b82f6;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 8px;
    transition: width 0.3s ease;
    min-width: 40px;
  }

  .bhp-progress-fill.complete {
    background: #1e40af;
  }

  .bhp-progress-text {
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
  }

  /* Charts Section - Compact */
  .bhp-charts-section {
    padding: 0 16px;
    margin-bottom: 16px;
  }

  .bhp-chart-row {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  .bhp-chart-card {
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 8px;
    padding: 12px;
  }

  .bhp-chart-full {
    grid-column: 1 / -1;
  }

  .bhp-chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }

  .bhp-chart-header h3 {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    margin: 0;
  }

  .bhp-chart-legend {
    display: flex;
    gap: 10px;
  }

  .bhp-legend-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.65rem;
    color: var(--text-secondary, #64748b);
  }

  .bhp-legend-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  .bhp-legend-bar {
    width: 12px;
    height: 6px;
    border-radius: 2px;
  }

  .bhp-dot-primary {
    background: #3b82f6;
  }

  .bhp-dot-secondary {
    background: #94a3b8;
  }

  .bhp-chart-body {
    height: 220px;
    position: relative;
  }

  .bhp-bar-chart {
    height: 160px;
  }

  .bhp-doughnut-container {
    height: 160px;
    position: relative;
  }

  .bhp-doughnut-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
  }

  .bhp-doughnut-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-primary, #1e293b);
    display: block;
  }

  .bhp-doughnut-label {
    font-size: 0.65rem;
    color: var(--text-secondary, #64748b);
    font-weight: 600;
    text-transform: uppercase;
  }

  .bhp-doughnut-legend {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 10px;
  }

  .bhp-doughnut-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.7rem;
    color: var(--text-secondary, #475569);
  }

  .bhp-doughnut-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  /* Table Section - Compact */
  .bhp-table-section {
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    margin: 0 16px 16px;
    border-radius: 8px;
    overflow: hidden;
  }

  .bhp-table-header {
    padding: 10px 16px;
    border-bottom: 1px solid var(--border-color, #e2e8f0);
    background: var(--bg-secondary, #f8fafc);
  }

  .bhp-table-header h3 {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    margin: 0;
  }

  .bhp-table-container {
    overflow-x: auto;
  }

  /* ====== TABLA UNIFICADA ====== */
  .bhp-table-unified {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
    font-size: 0.75rem;
  }

  /* Header Groups Row */
  .bhp-header-groups th {
    padding: 8px 10px;
    text-align: center;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: none;
  }

  .bhp-th-hora {
    background: #475569 !important;
    color: white !important;
    width: 80px;
    vertical-align: middle;
  }

  .bhp-th-pos {
    background: #2563eb !important;
    color: white !important;
  }

  .bhp-th-electronic {
    background: #0284c7 !important;
    color: white !important;
  }

  /* Header Columns Row */
  .bhp-header-columns th {
    padding: 6px 8px;
    text-align: center;
    font-size: 0.6rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .bhp-th-pos-sub {
    background: rgba(37, 99, 235, 0.1);
    color: #1e40af;
    border-right: 1px solid rgba(37, 99, 235, 0.15);
  }

  .bhp-th-pos-sub:last-of-type {
    border-right: 2px solid var(--border-color, #e2e8f0);
  }

  .bhp-th-elec-sub {
    background: rgba(2, 132, 199, 0.1);
    color: #0369a1;
    border-right: 1px solid rgba(2, 132, 199, 0.15);
  }

  .bhp-th-elec-sub:last-child {
    border-right: none;
  }

  /* Table Body */
  .bhp-table-unified tbody tr {
    border-bottom: 1px solid var(--border-color, #f1f5f9);
  }

  .bhp-table-unified tbody tr:hover {
    background: var(--bg-secondary, #f8fafc);
  }

  .bhp-table-unified tbody tr:last-child {
    border-bottom: none;
  }

  .bhp-table-unified td {
    padding: 6px 8px;
    font-size: 0.75rem;
    vertical-align: middle;
    text-align: center;
  }

  /* Row States */
  .bhp-row-current {
    background: rgba(59, 130, 246, 0.08) !important;
  }

  .bhp-row-past {
    background: var(--bg-secondary, #fafafa);
    opacity: 0.75;
  }

  .bhp-row-future {
    background: var(--bg-primary, #ffffff);
  }

  /* Hora Cell */
  .bhp-cell-hora {
    background: var(--bg-secondary, #f8fafc);
    border-right: 2px solid var(--border-color, #e2e8f0);
    font-weight: 600;
  }

  .bhp-hora-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .bhp-hora-time {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
  }

  .bhp-badge-now {
    background: #3b82f6;
    color: white;
    font-size: 0.5rem;
    font-weight: 600;
    padding: 1px 4px;
    border-radius: 3px;
  }

  /* POS Cells */
  .bhp-cell-pos {
    background: rgba(37, 99, 235, 0.02);
    border-right: 1px solid rgba(37, 99, 235, 0.08);
  }

  .bhp-cell-pos:nth-child(5) {
    border-right: 2px solid var(--border-color, #e2e8f0);
  }

  /* Electronic Cells */
  .bhp-cell-elec {
    background: rgba(2, 132, 199, 0.02);
    border-right: 1px solid rgba(2, 132, 199, 0.08);
  }

  .bhp-cell-elec:last-child {
    border-right: none;
  }

  /* Meta Cell */
  .bhp-meta-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
  }

  .bhp-meta-value {
    font-weight: 500;
    color: var(--text-primary, #374151);
    font-size: 0.75rem;
  }

  .bhp-meta-original {
    font-size: 0.6rem;
    color: var(--text-secondary, #9ca3af);
    font-style: italic;
  }

  /* Venta Cell */
  .bhp-venta-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
  }

  .bhp-venta-value {
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    font-size: 0.75rem;
  }

  .bhp-venta-count {
    font-size: 0.6rem;
    color: var(--text-secondary, #9ca3af);
  }

  /* Percent Badge - Compact */
  .bhp-percent-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
  }

  .bhp-percent-ahead {
    background: #1e40af;
    color: white;
  }

  .bhp-percent-on-track {
    background: #dbeafe;
    color: #1e40af;
  }

  .bhp-percent-behind {
    background: #bfdbfe;
    color: #1e3a8a;
  }

  /* Status Badge - Compact */
  .bhp-status-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.6rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .bhp-status-text {
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .bhp-status-ahead {
    background: #1e40af;
    color: white;
  }

  .bhp-status-on-track {
    background: #dbeafe;
    color: #1e40af;
  }

  .bhp-status-behind {
    background: #bfdbfe;
    color: #1e3a8a;
  }

  .bhp-sales-count {
    font-size: 0.65rem;
    color: var(--text-secondary, #94a3b8);
  }

  .bhp-cell-percent,
  .bhp-cell-percent-adaptive {
    text-align: center;
  }

  .bhp-cell-diff {
    font-weight: 600;
    font-size: 0.7rem;
  }

  .bhp-cell-diff.positive {
    color: #1e40af;
  }

  .bhp-cell-diff.negative {
    color: #64748b;
  }

  .bhp-diff-value {
    padding: 2px 6px;
    border-radius: 4px;
  }

  .bhp-cell-diff.positive .bhp-diff-value {
    background: rgba(30, 64, 175, 0.1);
  }

  .bhp-cell-diff.negative .bhp-diff-value {
    background: rgba(100, 116, 139, 0.1);
  }

  .bhp-status-indicator {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.6rem;
    font-weight: 600;
    white-space: nowrap;
  }

  /* Modal - Compact */
  .bhp-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
  }

  .bhp-modal {
    background: var(--bg-primary, #ffffff);
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 8px;
    width: 100%;
    max-width: 420px;
  }

  @keyframes modalSlide {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .bhp-modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border-color, #e2e8f0);
  }

  .bhp-modal-header h3 {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    margin: 0;
  }

  .bhp-modal-close {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--bg-secondary, #f1f5f9);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bhp-modal-close:hover {
    background: var(--border-color, #e2e8f0);
  }

  .bhp-modal-close svg {
    width: 14px;
    height: 14px;
    color: var(--text-secondary, #64748b);
  }

  .bhp-modal-body {
    padding: 16px;
  }

  .bhp-config-section {
    margin-bottom: 16px;
  }

  .bhp-config-section:last-child {
    margin-bottom: 0;
  }

  .bhp-config-section h4 {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-primary, #1e293b);
    margin: 0 0 10px 0;
  }

  .bhp-time-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .bhp-time-group label {
    display: block;
    font-size: 0.7rem;
    font-weight: 500;
    color: var(--text-secondary, #64748b);
    margin-bottom: 4px;
  }

  .bhp-time-group select {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--border-color, #e2e8f0);
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--text-primary, #1e293b);
    background: var(--bg-primary, #ffffff);
    cursor: pointer;
  }

  .bhp-time-group select:focus {
    outline: none;
    border-color: #3b82f6;
  }

  .bhp-config-description {
    font-size: 0.75rem;
    color: var(--text-secondary, #64748b);
    margin: 0 0 10px 0;
    line-height: 1.4;
  }

  .bhp-days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
  }

  .bhp-day-toggle {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 8px 4px;
    border-radius: 6px;
    background: var(--bg-secondary, #f1f5f9);
    color: var(--text-secondary, #64748b);
    cursor: pointer;
    border: 1px solid transparent;
    text-align: center;
  }

  .bhp-day-toggle input {
    display: none;
  }

  .bhp-day-short {
    font-size: 0.85rem;
    font-weight: 600;
    line-height: 1;
  }

  .bhp-day-full {
    font-size: 0.55rem;
    font-weight: 500;
    margin-top: 2px;
    text-transform: uppercase;
  }

  .bhp-day-toggle:hover {
    background: var(--border-color, #e2e8f0);
  }

  .bhp-day-toggle.active {
    background: #3b82f6;
    color: white;
    border-color: #2563eb;
  }

  .bhp-day-toggle.active:hover {
    background: #2563eb;
  }

  .bhp-days-summary {
    margin-top: 10px;
    padding: 8px;
    background: #f8fafc;
    border-radius: 8px;
    text-align: center;
  }

  .bhp-days-count {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1e40af;
  }

  .bhp-modal-footer {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding: 12px 16px;
    border-top: 1px solid var(--border-color, #e2e8f0);
    background: var(--bg-secondary, #f8fafc);
  }

  .bhp-btn {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
    border: 1px solid transparent;
  }

  .bhp-btn-secondary {
    background: var(--bg-primary, #ffffff);
    color: var(--text-secondary, #64748b);
    border-color: var(--border-color, #e2e8f0);
  }

  .bhp-btn-secondary:hover {
    background: var(--bg-secondary, #f1f5f9);
  }

  .bhp-btn-primary {
    background: #3b82f6;
    color: white;
    border-color: #2563eb;
  }

  .bhp-btn-primary:hover {
    background: #2563eb;
  }

  .bhp-btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Loading & Error States */
  .bhp-loading,
  .bhp-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    color: var(--text-secondary, #64748b);
    font-size: 0.8rem;
  }

  .bhp-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--border-color, #e2e8f0);
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 10px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .bhp-table-loading,
  .bhp-table-empty {
    padding: 1.5rem;
    text-align: center;
    color: var(--text-secondary, #64748b);
    font-size: 0.75rem;
  }

  /* Responsive */
  @media (max-width: 1200px) {
    .bhp-metrics-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .bhp-chart-row {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .bhp-header {
      flex-direction: column;
      gap: 10px;
      text-align: center;
    }

    .bhp-info-bar {
      flex-wrap: wrap;
      gap: 10px;
    }

    .bhp-info-divider {
      display: none;
    }

    .bhp-metrics-grid {
      grid-template-columns: 1fr;
    }

    .bhp-date-selector {
      flex-wrap: wrap;
    }

    .bhp-panel {
      padding: 10px;
    }
  }
`;
